import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Save, KeyRound, AtSign, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API } from '../lib/api';

export function AdminSettings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newEmail && !newPassword) {
      toast.error('Preencha pelo menos um campo (email ou senha)');
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (!currentPassword) {
      toast.error('Informe sua senha atual para confirmar a alteração');
      return;
    }

    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (newEmail) payload.new_email = newEmail;
      if (newPassword) payload.new_password = newPassword;

      await axios.post(`${API}/admin/update-credentials`, payload, { withCredentials: true });
      toast.success('Credenciais atualizadas! Faça login novamente com as novas credenciais.');

      // Force logout so user must login again
      setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar credenciais');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="settings-section"
      className="max-w-2xl"
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Configurações da Conta Admin</h2>
        <p className="text-zinc-400 text-sm mt-1">
          Atualize seu email e senha de acesso. Você precisará fazer login novamente após salvar.
        </p>
      </div>

      <div className="backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-xl p-6 mb-6">
        <div className="flex items-center space-x-3 text-zinc-400 text-sm">
          <ShieldCheck className="w-5 h-5 text-[#00FF99]" />
          <span>
            Conta atual: <span className="text-white font-semibold">{user?.email}</span>
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-xl p-6 space-y-5">
        <div>
          <label className="block text-sm font-semibold text-white mb-2">
            <span className="inline-flex items-center space-x-2">
              <AtSign className="w-4 h-4 text-[#0066FF]" />
              <span>Novo Email</span>
              <span className="text-zinc-500 font-normal text-xs">(opcional)</span>
            </span>
          </label>
          <input
            data-testid="new-email-input"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder={user?.email || 'seu@email.com'}
            className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-white mb-2">
            <span className="inline-flex items-center space-x-2">
              <KeyRound className="w-4 h-4 text-[#0066FF]" />
              <span>Nova Senha</span>
              <span className="text-zinc-500 font-normal text-xs">(opcional)</span>
            </span>
          </label>
          <input
            data-testid="new-password-input"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Deixe em branco para manter a atual"
            className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
          />
        </div>

        {newPassword && (
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Confirme a Nova Senha</label>
            <input
              data-testid="confirm-password-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
            />
          </div>
        )}

        <div className="pt-4 border-t border-[#27272A]">
          <label className="block text-sm font-semibold text-white mb-2">
            <span className="inline-flex items-center space-x-2">
              <KeyRound className="w-4 h-4 text-[#00FF99]" />
              <span>Senha Atual</span>
              <span className="text-red-400 font-normal text-xs">(obrigatória)</span>
            </span>
          </label>
          <input
            data-testid="current-password-input"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            placeholder="Confirme com sua senha atual"
            className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#00FF99] transition-colors"
          />
        </div>

        <button
          type="submit"
          data-testid="save-credentials-button"
          disabled={saving}
          className="w-full flex items-center justify-center space-x-2 bg-[#0066FF] text-white hover:bg-[#3385FF] transition-all duration-300 font-bold tracking-wide rounded-lg px-6 py-3 shadow-[0_0_20px_rgba(0,102,255,0.4)] hover:shadow-[0_0_30px_rgba(0,102,255,0.6)] disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          <span>{saving ? 'Salvando...' : 'Salvar Alterações'}</span>
        </button>
      </form>
    </motion.div>
  );
}
